import countries from "i18n-iso-countries";

/**
 * ISO country code → the country's name, in `locale`.
 *
 * `Intl.DisplayNames` is the whole implementation; this fixes one answer for
 * each edge so every caller agrees:
 *
 * - No code at all → null. Callers that want `""` or `undefined` say so.
 * - Resolved → the name.
 * - Not resolvable, or the runtime rejects the code → the code itself, so a
 *   reader sees `SVN` rather than a blank where a country belongs.
 *
 * Alpha-2 only, which is what `Intl.DisplayNames` accepts — it throws on a
 * three-letter input. Convert with `alpha3ToAlpha2` first when the source is
 * MRZ data.
 *
 * English by default because most call sites are a staff-facing screen or a
 * line a sorting office reads. Pass a locale where the name lands inside a
 * sentence somebody else reads: a country named in English in the middle of a
 * Slovene paragraph is the mistake.
 */
export function countryName(
  code: string | null | undefined,
  locale = "en"
): string | null {
  if (!code) {
    return null;
  }
  try {
    return (
      new Intl.DisplayNames([locale], { type: "region" }).of(
        code.toUpperCase()
      ) ?? code
    );
  } catch {
    return code;
  }
}

/**
 * Nationality codes ICAO 9303 prints in a machine-readable zone that are not
 * ISO 3166 alpha-3, mapped to the country whose citizens carry them: Germany's
 * passports carry `D`, Kosovo's `RKS`.
 */
export const ICAO_NATIONALITY_TO_ALPHA2: Readonly<Record<string, string>> = {
  D: "DE",
  RKS: "XK",
};

/** An MRZ country code without the zone's `<` filler or stray whitespace. */
export function bareCountryCode(code: string): string {
  return code.replace(/[<\s]/gu, "").toUpperCase();
}

/**
 * An MRZ country code (ISO 3166-1 alpha-3, or an ICAO code outside it) →
 * alpha-2, or null if unmappable.
 */
export function alpha3ToAlpha2(code: string | null | undefined): string | null {
  if (!code) {
    return null;
  }
  const bare = bareCountryCode(code);
  return (
    ICAO_NATIONALITY_TO_ALPHA2[bare] ?? countries.alpha3ToAlpha2(bare) ?? null
  );
}

/** ISO 3166-1 alpha-2 → alpha-3 (MRZ / ICAO), or null if unmappable. */
export function alpha2ToAlpha3(
  alpha2: string | null | undefined
): string | null {
  if (!alpha2) {
    return null;
  }
  return countries.alpha2ToAlpha3(alpha2.toUpperCase()) ?? null;
}
